import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { Switch as SwitchPrimitive } from 'radix-ui';

import { useFocusRing } from '@components/primitives/FocusRing';
import { useFormField } from '@components/primitives/FormField';
import { cn } from '@components/primitives/utilities';

import { SwitchContext } from './context';
import { SwitchLabel } from './SwitchLabel';
import { SwitchThumb } from './SwitchThumb';

import styles from './styles.module.css';

export interface SwitchProps
  extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  size?: 'sm' | 'lg';
}

/**
 * Its parts — `Switch.Thumb`, `Switch.Label` — render in the order they are
 * written. Children are optional: a `<Switch>` given none renders a bare
 * `Switch.Thumb`, which is the common case.
 */
const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { id: propId, size = 'sm', className, style, children, ...props },
  ref
) {
  const form = useFormField();
  const localId = useId();
  const id = propId ?? form?.id ?? localId;

  const labelId = useId();
  const [hasLabel, setHasLabel] = useState(false);

  const registerLabel = useCallback(() => {
    setHasLabel(true);
    return () => setHasLabel(false);
  }, []);

  const [switchRef, ringStyle] = useFocusRing<HTMLButtonElement>();

  const context = useMemo(
    () => ({ labelId, registerLabel }),
    [labelId, registerLabel]
  );

  return (
    <SwitchContext.Provider value={context}>
      <SwitchPrimitive.Root
        // Rest props last: Restricted clones this with tooltip and aria props.
        ref={composeRefs(ref, switchRef)}
        id={id}
        aria-invalid={form?.error ? true : undefined}
        aria-labelledby={hasLabel ? labelId : undefined}
        {...props}
        className={cn(styles.root, styles[size], className)}
        style={{ ...ringStyle, ...style }}
      >
        {children ?? <SwitchThumb />}
      </SwitchPrimitive.Root>
    </SwitchContext.Provider>
  );
});

const SwitchNamespace = Object.assign(Switch, {
  Thumb: SwitchThumb,
  Label: SwitchLabel,
});

export { SwitchNamespace as Switch };

export type { SwitchLabelProps } from './SwitchLabel';
export type { SwitchThumbProps } from './SwitchThumb';
